import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BadgePercent, CheckCheck } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

const POLL_MS = 60000;

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const loadCount = () => api.notificationsUnread().then((d) => setUnread(d.unread_count || 0)).catch(() => {});
  const loadAll = () => api.notifications().then((d) => { setItems(d.items || []); setUnread(d.unread_count || 0); }).catch(() => {});

  useEffect(() => {
    loadCount();
    timer.current = setInterval(loadCount, POLL_MS);
    return () => clearInterval(timer.current);
  }, []);

  useEffect(() => { if (open) loadAll(); }, [open]);

  const markAllRead = async () => {
    await api.notificationsReadAll().catch(() => {});
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const onItemClick = async (n) => {
    if (!n.read) {
      await api.notificationRead(n.id).catch(() => {});
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-300 hover:border-gray-400 text-gray-600 transition-colors"
          aria-label="Notifications"
          data-testid="notification-bell"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D32323] text-white text-[10px] font-bold inline-flex items-center justify-center"
              data-testid="notification-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-dropdown">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="font-heading font-bold text-sm text-gray-900">Notifications</span>
          {items.some((n) => !n.read) && (
            <button onClick={markAllRead} className="inline-flex items-center gap-1 text-xs text-[#00AA6C] hover:underline" data-testid="notification-mark-all">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-500" data-testid="notification-empty">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
              No notifications yet. Save your favourite places to hear about their new offers.
            </div>
          ) : (
            items.map((n) => (
              <Link
                key={n.id}
                to={n.link || '#'}
                onClick={() => onItemClick(n)}
                className={cn('flex gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors', !n.read && 'bg-emerald-50/40')}
                data-testid={`notification-item-${n.id}`}
              >
                {n.image ? (
                  <img src={n.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" loading="lazy" />
                ) : (
                  <span className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 inline-flex items-center justify-center shrink-0">
                    <BadgePercent className="w-5 h-5" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 leading-tight">{n.title}</div>
                  {n.body && <div className="text-xs text-gray-600 line-clamp-2 mt-0.5">{n.body}</div>}
                  <div className="text-[10px] text-gray-400 mt-1">{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#00AA6C] mt-1.5 shrink-0" />}
              </Link>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
